import { useCallback, useEffect, useState } from "react"

import type { LocalMediaAttachment } from "../components/MediaAttachmentSection"
import type { EditableTestCase } from "../model"

/**
 * Manages per-test-case local media file selections that are staged in the
 * editor and uploaded only when the user saves.  Keeps the map in sync with
 * the current test-case list so stale entries are pruned automatically when
 * rows are deleted.
 */
export function useLocalMediaAttachments(testCases: EditableTestCase[]) {
  const [
    localMediaAttachmentsByTestCaseId,
    setLocalMediaAttachmentsByTestCaseId,
  ] = useState<Record<string, LocalMediaAttachment>>({})

  // Drop entries for test cases that no longer exist.
  useEffect(() => {
    const validIds = new Set(testCases.map((tc) => tc.id))
    setLocalMediaAttachmentsByTestCaseId((prev) => {
      const nextEntries = Object.entries(prev).filter(([id]) =>
        validIds.has(id),
      )
      if (nextEntries.length === Object.keys(prev).length) {
        return prev
      }
      return Object.fromEntries(nextEntries)
    })
  }, [testCases])

  const getLocalMediaAttachment = useCallback(
    (testCaseId: string) => {
      return localMediaAttachmentsByTestCaseId[testCaseId] ?? null
    },
    [localMediaAttachmentsByTestCaseId],
  )

  const setLocalMediaAttachment = useCallback(
    (testCaseId: string, attachment: LocalMediaAttachment | null) => {
      setLocalMediaAttachmentsByTestCaseId((prev) => {
        if (!attachment) {
          if (!prev[testCaseId]) {
            return prev
          }
          const next = { ...prev }
          delete next[testCaseId]
          return next
        }
        return { ...prev, [testCaseId]: attachment }
      })
    },
    [],
  )

  const clearLocalMediaAttachments = useCallback(() => {
    setLocalMediaAttachmentsByTestCaseId({})
  }, [])

  return {
    localMediaAttachmentsByTestCaseId,
    getLocalMediaAttachment,
    setLocalMediaAttachment,
    clearLocalMediaAttachments,
  }
}
